import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';

import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath, STALE_TIMES } from '@ais-ui/custom-hooks';

import {
  Configuration,
  DetailedShipList,
  DetailedShipListToJSON,
  type GetShiplists200Response,
  ShipList,
  ShiplistApi,
} from '@ais-ui/api-spec/config';
import { getBasePath } from './getAISBasePath';
import { useApiClientWrapper } from './apiClientWrapper';
import { useServiceAvailability } from './ServiceAvailabilityProvider';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';

export interface ShiplistMutateInput {
  shiplist: ShipList;
  useFillShipList?: boolean;
}

/**
 * Build initOverrides that inject `use_fill_ship_list` into the request body.
 * The runtime JSON.stringifies plain-object bodies, so we pass the merged
 * object directly — no manual stringify needed.
 */
function buildFillShipListOverride(
  shiplist: ShipList,
  useFillShipList?: boolean,
): RequestInit | undefined {
  if (!useFillShipList) return undefined;
  return {
    body: {
      ...DetailedShipListToJSON(shiplist),
      use_fill_ship_list: true,
    },
  } as RequestInit;
}

const useShiplistApi = (): ShiplistApi => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();
  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/config`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/config`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const shiplistApi = useMemo(
    () => new ShiplistApi(configuration),
    [configuration],
  );
  // The wrapper is used to get error messages from the backend, as they are otherwise thrown out
  return apiClientWrapper(shiplistApi) as ShiplistApi;
};

export const useFetchAllShiplists =
  (): UseQueryResult<GetShiplists200Response> => {
    const shiplistApi = useShiplistApi();
    const { isAvailable } = useServiceAvailability();

    return useQuery(
      {
        queryKey: ['FetchAllShiplists'],
        queryFn: async () => {
          return await shiplistApi.getShiplists();
        },
        staleTime: STALE_TIMES.FREQUENT,
        refetchInterval: false,
        refetchOnWindowFocus: false,
        enabled: isAvailable('shiplist'),
      },
      'shiplists:list',
    );
  };

export const useGetShiplist = (
  id: string,
): UseQueryResult<DetailedShipList> => {
  const shiplistApi = useShiplistApi();
  return useQuery(
    {
      queryKey: ['FetchShiplist', id],
      queryFn: async () => {
        return await shiplistApi.getShiplist({ id });
      },
    },
    'shiplists:get',
  );
};

export const useAddShiplist = (): UseMutationResult<
  void,
  { response?: any },
  ShiplistMutateInput,
  unknown
> => {
  const shiplistApi = useShiplistApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ shiplist, useFillShipList }: ShiplistMutateInput) => {
        return shiplistApi.addShiplist(
          { json: shiplist },
          buildFillShipListOverride(shiplist, useFillShipList),
        );
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['FetchAllShiplists'] }),
    },
    'shiplist:create',
  );
};

export const useUpdateShiplist = (): UseMutationResult<
  void,
  { response?: any },
  ShiplistMutateInput,
  unknown
> => {
  const shiplistApi = useShiplistApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: ({ shiplist, useFillShipList }: ShiplistMutateInput) => {
        return shiplistApi.updateShiplist(
          { detailedShipList: shiplist },
          buildFillShipListOverride(shiplist, useFillShipList),
        );
      },
      onSuccess: (data, { shiplist }) => {
        queryClient.invalidateQueries({ queryKey: ['FetchAllShiplists'] });
        queryClient.invalidateQueries({
          queryKey: ['FetchShiplist', shiplist.id],
        });
      },
    },
    'shiplist:update',
  );
};

export const useDeleteShiplist = (): UseMutationResult<
  void,
  Error,
  string,
  unknown
> => {
  const shiplistApi = useShiplistApi();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: (id: string) => {
        return shiplistApi.shiplistIdDelete({ id: id });
      },
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: ['FetchAllShiplists'] }),
    },
    'shiplist:delete',
  );
};
